import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatInTimeZone } from 'date-fns-tz';
import { memo, useMemo } from 'react';

import { Button, TabSwitch, TabSwitchList, TabSwitchTab } from '@/ui';

import type { CalendarView } from '../../types';
import { getVisibleRange, navigateDate } from '../../utils/dateGrid';

import type { CalendarHeaderProps } from './types';

const VIEW_LABELS: Record<CalendarView, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  agenda: 'Agenda'
};

function getRangeLabel(date: Date, view: CalendarView, timeZone: string): string {
  const { start, end } = getVisibleRange(date, view, timeZone);
  switch (view) {
    case 'day':
      return formatInTimeZone(date, timeZone, 'EEEE, MMMM d, yyyy');
    case 'month':
      return formatInTimeZone(date, timeZone, 'MMMM yyyy');
    case 'week':
    case 'agenda':
      return `${formatInTimeZone(start, timeZone, 'MMM d')} – ${formatInTimeZone(end, timeZone, 'MMM d, yyyy')}`;
  }
}

function CalendarHeader({
  view,
  onViewChange,
  availableViews,
  date,
  onNavigate,
  timeZone
}: CalendarHeaderProps) {
  const rangeLabel = useMemo(() => getRangeLabel(date, view, timeZone), [date, view, timeZone]);
  const selectedIndex = Math.max(availableViews.indexOf(view), 0);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          color="neutral"
          size="sm"
          onClick={() => onNavigate(new Date())}
          aria-label="Today">
          Today
        </Button>
        <Button
          variant="ghost"
          color="neutral"
          size="icon-md"
          onClick={() => onNavigate(navigateDate(date, view, -1, timeZone))}
          aria-label="Previous">
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <Button
          variant="ghost"
          color="neutral"
          size="icon-md"
          onClick={() => onNavigate(navigateDate(date, view, 1, timeZone))}
          aria-label="Next">
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
        <span className="text-sm font-semibold text-uniform-gray-900 dark:text-uniform-gray-100">
          {rangeLabel}
        </span>
      </div>

      <TabSwitch
        variant="pills"
        size="sm"
        className="mr-2"
        selectedIndex={selectedIndex}
        onChange={(index) => onViewChange(availableViews[index])}>
        <TabSwitchList>
          {availableViews.map((availableView) => (
            <TabSwitchTab key={availableView}>{VIEW_LABELS[availableView]}</TabSwitchTab>
          ))}
        </TabSwitchList>
      </TabSwitch>
    </div>
  );
}

export default memo(CalendarHeader);
